import * as _ from "lodash";
import * as React from "react";
import { OverlayTrigger, Popover, Row } from "react-bootstrap";
import * as semver from "semver";
import { Utilities } from "../../shared/utilities";

import { setLastReadVersion } from "../actions/AppActions";
import { AppContext, releaseNoteStorageKey } from "../App";
import { IAppReducerState } from "../shared/model";
import NotificationMessageNotifier from "./NotificationMessageNotifier";

const NotificationCenter: React.FC = () => {
    const target = React.useRef(null);

    const { appState, dispatch } = React.useContext<IAppReducerState>(AppContext);

    const [isShowingPopover, setIsShowingPopover] = React.useState<boolean>(false);
    const [releaseNote, setReleaseNote] = React.useState<IReleaseNote>();

    React.useEffect(() => {
        const fetchCoreVersion = async () => {
            try {
                const coreVersion = await Utilities.getFetch<string>("api/navigation/getUIVersion"); // 5.4.2.1337
                const splitVersion: string[] = coreVersion.split(".").slice(0, -1); // remove pre-release number
                const semanticJoin = splitVersion.join("."); // 5.4.2
                const urlJoin = splitVersion.join(""); // 542
                setReleaseNote({
                    version: semanticJoin,
                    intercomLink: `https://docs.frends.com/en/articles/4953793-5-4-release-notes#${urlJoin}`
                });
            } catch (e) {
                console.error(e);
                Utilities.toastError(e);
            }
        }
        fetchCoreVersion();
    }, [])

    const [isTriggeringAnimation, setIsTriggeringAnimation] = React.useState<boolean>(false);

    const shouldTriggerAnimation = () => {
        if (
            !_.isEmpty(appState?.notificationMessages) && appState?.notificationMessages.some((s) => s.viewed !== true) ||
            appState?.lastReadVersion !== "" && !_.isEqual(appState?.lastReadVersion, releaseNote?.version)
        ) {
            return true;
        } else {
            return false;
        }
    };

    React.useEffect(() => {
        setIsTriggeringAnimation(shouldTriggerAnimation());
    }, [appState]);

    React.useEffect(() => {
        if (isShowingPopover) {
            setLastReadVersion(dispatch, (releaseNote?.version ?? null), releaseNoteStorageKey); // null-coalesce returns undefined, but setLastReadVersion expect string | null
            setIsTriggeringAnimation(shouldTriggerAnimation());
        }
    }, [isShowingPopover]);

    const toggleIsShowingPopover = () => {
        setIsShowingPopover(!isShowingPopover);
    };

    const popOver = (
        <Popover
            id="navigation-notification-popover"
            style={{ padding: 0 }}
        >
            <NotificationMessageNotifier
                // appState and dispatch can't be read in NotificationMessageNotifier cause it's not a direct child, so that's why we pass them as props
                state={{ appState, dispatch }}
            />
            <div style={{ overflowY: "auto" }}>
                <Row>
                    <h4 style={{ marginTop: 0 }}>
                        <strong>Release notes</strong>
                    </h4>
                    <a href={releaseNote?.intercomLink} target="_blank" className="italic-blue-link">
                        <p>Release notes for {semver.valid(semver.coerce(releaseNote?.version))}</p>
                    </a>
                    <a target="_blank" className="italic-blue-link" href="https://docs.frends.com/en/collections/1192384-release-notes">All release notes</a>
                </Row>
            </div>
        </Popover>
    );

    return (
        <li role="presentation" id="navigation-notification" ref={target} onClick={toggleIsShowingPopover}>
            <OverlayTrigger trigger="click" placement="bottom" overlay={popOver} containerPadding={20} rootClose={true}>
                <p className="navbar-text">
                    <i className="fa fa-bell pointer" id={`navigation-changelog-bell${isTriggeringAnimation ? "-animating" : ""}`}></i>
                </p>
            </OverlayTrigger>
            {isTriggeringAnimation && <div id="navigation-changelog-badge" />}
        </li>
    );
};

interface IReleaseNote {
    version: string;
    intercomLink: string;
}

export default NotificationCenter;
